import { definePreset, palette } from '@openng/optimus-ui-themes';
import type { Preset } from '@openng/optimus-ui-themes/types';

import Lara from '@openng/optimus-ui-themes/lara';

/** Tag severities resolve to the `status.*` semantic tokens, which flip per color scheme. */
const statusTagColors = {
  success: {
    background: '{status.success.background}',
    color: '{status.success.color}',
  },
  warn: {
    background: '{status.warn.background}',
    color: '{status.warn.color}',
  },
  danger: {
    background: '{status.danger.background}',
    color: '{status.danger.color}',
  },
  info: {
    background: '{status.info.background}',
    color: '{status.info.color}',
  },
  secondary: {
    background: '{status.neutral.background}',
    color: '{status.neutral.color}',
  },
};

/**
 * Button focus ring. Lara draws it as a pale 3px shadow per severity (about
 * 1.3:1 on white); this one follows the semantic focus ring colour, which
 * measures 3:1 or better in both colour schemes.
 */
const buttonFocusRing = {
  focusRing: {
    color: 'transparent',
    shadow: '0 0 0 0.2rem {focus.ring.color}',
  },
};
const otherButtonSeverities = Object.fromEntries(
  ['info', 'success', 'warn', 'help', 'danger', 'contrast'].map((severity) => [
    severity,
    buttonFocusRing,
  ]),
);

// Create a custom theme based on Lara that matches the old SagaBlue theme
export const padspinPrimengPreset = definePreset(Lara, {
  // Define primitive tokens
  primitive: {
    blue: palette('#0659a9'), // Your primary blue color
    green: palette('#2ecc71'), // Loveable brand green (emerald)
  },

  // Define semantic tokens
  semantic: {
    primary: palette('#0659a9'),

    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },

    // Color scheme specific tokens
    colorScheme: {
      light: {
        primary: {
          color: '{blue.500}', // Using your primary blue
          contrastColor: '#ffffff',
          hoverColor: '{blue.400}',
          activeColor: '{blue.300}',
        },
        // Mint/cool-tinted neutral surface (low chroma, green undertone) so
        // surfaces keep the Loveable softness. Single source of truth —
        // PrimeNG flips to the dark scale automatically.
        surface: {
          0: '#ffffff',
          50: '#f3f8f6',
          100: '#e7f0ec',
          200: '#d3e2dc',
          300: '#b3c9c0',
          400: '#8aa79c',
          500: '#688478',
          600: '#50685e',
          700: '#3f5249',
          800: '#2c3a34',
          900: '#1d2723',
          950: '#121815',
        },
        highlight: {
          background: '{blue.100}',
          focusBackground: '{blue.200}',
          color: '{blue.500}',
          focusColor: '{blue.500}',
        },
        formField: {
          placeholderColor: '{surface.600}',
        },
        status: {
          success: {
            background: '{emerald.50}',
            borderColor: '{emerald.200}',
            color: '{emerald.700}',
          },
          warn: {
            background: '{amber.50}',
            borderColor: '{amber.200}',
            color: '{amber.800}',
          },
          danger: {
            background: '{rose.50}',
            borderColor: '{rose.200}',
            color: '{rose.800}',
          },
          info: {
            background: '{blue.50}',
            borderColor: '{blue.200}',
            color: '{blue.700}',
          },
          neutral: {
            background: '{surface.100}',
            borderColor: '{surface.200}',
            color: '{surface.700}',
          },
        },
        // Strong near-ink foreground (the Loveable design's heading color);
        // PrimeNG's default surface-700 text read too muted for titles.
        // `text-color` → headings/strong text, `text-muted-color` → body.
        text: {
          color: 'oklch(0.18 0.04 200)',
          hoverColor: 'oklch(0.18 0.04 200)',
          mutedColor: 'oklch(0.5 0.04 220)',
          hoverMutedColor: 'oklch(0.5 0.04 220)',
        },
      },
      dark: {
        primary: {
          color: '{blue.500}',
          contrastColor: '#ffffff',
          hoverColor: '{blue.400}',
          activeColor: '{blue.300}',
        },
        // Cool slate dark surface, pairs with the mint light surface.
        surface: {
          0: '#ffffff',
          50: '#e9eef0',
          100: '#cdd6da',
          200: '#a7b5bb',
          300: '#7c8e95',
          400: '#5b6d73',
          500: '#445157',
          600: '#333f44',
          700: '#28333a',
          800: '#1b242a',
          900: '#121a1f',
          950: '#0c1216',
        },
        highlight: {
          background: '{blue.800}',
          focusBackground: '{blue.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
        focusRing: {
          color: '{blue.300}',
        },
        formField: {
          placeholderColor: '{surface.300}',
        },
        status: {
          success: {
            background: 'color-mix(in srgb, {emerald.400}, transparent 85%)',
            borderColor: 'color-mix(in srgb, {emerald.400}, transparent 70%)',
            color: '{emerald.300}',
          },
          warn: {
            background: 'color-mix(in srgb, {amber.400}, transparent 85%)',
            borderColor: 'color-mix(in srgb, {amber.400}, transparent 70%)',
            color: '{amber.300}',
          },
          danger: {
            background: 'color-mix(in srgb, {rose.400}, transparent 85%)',
            borderColor: 'color-mix(in srgb, {rose.400}, transparent 70%)',
            color: '{rose.300}',
          },
          info: {
            background: 'color-mix(in srgb, {blue.400}, transparent 85%)',
            borderColor: 'color-mix(in srgb, {blue.400}, transparent 70%)',
            color: '{blue.300}',
          },
          neutral: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
            color: '{surface.200}',
          },
        },
        // Light foreground for dark mode (the Loveable dark palette).
        text: {
          color: 'oklch(0.984 0.003 247.858)',
          hoverColor: 'oklch(0.984 0.003 247.858)',
          mutedColor: 'oklch(0.704 0.04 256.788)',
          hoverMutedColor: 'oklch(0.704 0.04 256.788)',
        },
      },
    },
  },

  components: {
    datatable: {},
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '600',
        padding: '0.125rem 0.625rem',
        borderRadius: '9999px',
      },
      colorScheme: {
        light: statusTagColors,
        dark: statusTagColors,
      },
      css: `
.p-tag { box-shadow: inset 0 0 0 1px color-mix(in srgb, currentColor 22%, transparent); }
.p-tag-icon.pi-circle-fill { font-size: 0.375rem; }
`,
    },
    badge: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '600',
        height: '1.25rem',
        minWidth: '1.25rem',
        padding: '0 0.375rem',
      },
    },
    chip: {
      root: {
        borderRadius: '9999px',
        paddingX: '0.625rem',
        paddingY: '0.25rem',
        gap: '0.375rem',
      },
    },
    avatar: {
      root: {
        width: '1.5rem',
        height: '1.5rem',
        fontSize: '0.6875rem',
      },
    },
    card: {
      root: {
        borderRadius: '1rem',
        shadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      },
      body: {
        padding: '1rem',
        gap: '0.5rem',
      },
      css: `
.p-card { border: 1px solid var(--p-content-border-color); }
`,
    },
    menu: {
      item: {
        padding: '12px 16px 12px 16px',
      },
      list: {
        // padding: '12px 16px 12px 16px',
      },
    },
    button: {
      // Extra-rounded corners site-wide (matches the old gradient-button's
      // `rounded-xl`). Pill buttons (`[rounded]`) are unaffected — they use
      // the separate `roundedBorderRadius` token.
      root: {
        borderRadius: '0.75rem',
      },
      // The default/primary button is the brand green→sky gradient site-wide,
      // replacing the old `padspinGradientButton` directive. The gradient is
      // set via the `primary.background` tokens below; the soft "glow" shadow
      // lives here because PrimeNG has no resting box-shadow token. The long
      // `:not()` chain scopes the glow to the *default* button only (no
      // severity, not outlined/text/link/raised) — those keep their own look.
      css: `
.p-button:not(.p-button-secondary):not(.p-button-success):not(.p-button-info):not(.p-button-warn):not(.p-button-help):not(.p-button-danger):not(.p-button-contrast):not(.p-button-outlined):not(.p-button-text):not(.p-button-link):not(.p-button-raised) {
    box-shadow: 0 20px 60px -20px color-mix(in srgb, var(--p-primary-500), transparent 55%);
}
.p-button:not(.p-button-secondary):not(.p-button-success):not(.p-button-info):not(.p-button-warn):not(.p-button-help):not(.p-button-danger):not(.p-button-contrast):not(.p-button-outlined):not(.p-button-text):not(.p-button-link):not(.p-button-raised):not(:disabled):hover {
    box-shadow: 0 10px 40px -12px rgb(0 0 0 / 0.12);
}
`,
      colorScheme: {
        light: {
          root: {
            // Brand green→sky gradient. Hover/active darken a step for
            // interactive feedback (the old directive used opacity).
            ...otherButtonSeverities,
            primary: {
              ...buttonFocusRing,
              color: '#ffffff',
              background:
                'linear-gradient(135deg, {green.500} 0%, {sky.500} 100%)',
              borderColor: 'transparent',
              hoverBackground:
                'linear-gradient(135deg, {green.600} 0%, {sky.600} 100%)',
              hoverColor: '#ffffff',
              hoverBorderColor: 'transparent',
              activeBackground:
                'linear-gradient(135deg, {green.700} 0%, {sky.700} 100%)',
              activeColor: '#ffffff',
              activeBorderColor: 'transparent',
            },
            secondary: {
              ...buttonFocusRing,
              color: '#ffffff',
              background: '{green.500}',
              borderColor: 'transparent',
              hoverBackground: '{green.300}',
              hoverColor: '#ffffff',
              hoverBorderColor: 'transparent',
              activeBackground: '{green.400}',
              activeColor: '#ffffff',
              activeBorderColor: 'transparent',
            },
          },
          outlined: {
            secondary: {
              color: '{green.500}',
              borderColor: '{green.500}',
              activeBackground: '{green.200}',
              hoverBackground: '{green.100}',
            },
            contrast: {
              color: '#ffffff',
              borderColor: '#ffffff',
              activeBackground: 'rgba(255,255,255,0.2)',
              hoverBackground: 'rgba(255,255,255,0.1)',
            },
          },
        },
        dark: {
          // The brand gradient is mode-independent — green/sky are primitive
          // tokens, identical in both schemes — so dark mode gets the same
          // primary button as light.
          root: {
            ...otherButtonSeverities,
            secondary: buttonFocusRing,
            primary: {
              ...buttonFocusRing,
              color: '#ffffff',
              background:
                'linear-gradient(135deg, {green.500} 0%, {sky.500} 100%)',
              borderColor: 'transparent',
              hoverBackground:
                'linear-gradient(135deg, {green.600} 0%, {sky.600} 100%)',
              hoverColor: '#ffffff',
              hoverBorderColor: 'transparent',
              activeBackground:
                'linear-gradient(135deg, {green.700} 0%, {sky.700} 100%)',
              activeColor: '#ffffff',
              activeBorderColor: 'transparent',
            },
          },
        },
      },
    },
    fileupload: {
      colorScheme: {
        light: {
          header: {
            background: '{surface.50}',
            borderWidth: '0',
          },
          root: {
            background: '{surface.50}',
          },
        },
      },
    },
  },
} satisfies Preset);
